import { useEffect, useState } from 'react';

import { useOrganization } from '@clerk/clerk-react';

// import type { OrganizationResource } from '@clerk/types';
import type { MemberData } from './types';

// Organization の状態を表す型
export interface CurrentOrganization {
  isLoaded: boolean;
  organization: ReturnType<typeof useOrganization>['organization'];
  membership: ReturnType<typeof useOrganization>['membership'];
  // 必要に応じて isAdmin などの派生状態を追加
  isAdmin: boolean | undefined;
}

/**
 * 現在アクティブな組織とそのメンバーシップ情報を提供するフック
 */
export function useCurrentOrganization(): CurrentOrganization {
  const { isLoaded, organization, membership } = useOrganization();

  // isLoaded が true になるまで organization, membership は null の可能性がある
  const isAdmin =
    isLoaded && membership ? membership.role === 'org:admin' : undefined;

  return {
    isLoaded,
    organization,
    membership,
    isAdmin,
  };
}

// ★ Define the structure for doctor options
interface DoctorOption {
  value: string;
  label: string;
}

// ★ Define the return type for the new hook
interface UseOrganizationMembersReturn {
  members: MemberData[];
  doctorOptions: DoctorOption[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * 現在の組織のメンバーリストとそのドロップダウンオプションを提供するフック
 * @param selectedDeptId - (Optional) Filter members by this department ID.
 */
export function useOrganizationMembers(
  selectedDeptId: string | null = null // ★ Add selectedDeptId as optional argument
): UseOrganizationMembersReturn {
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const [members, setMembers] = useState<MemberData[]>([]);
  const [doctorOptions, setDoctorOptions] = useState<DoctorOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isOrgLoaded) {
      setIsLoading(true);
      return;
    }

    if (!organization) {
      // No organization selected or available
      setMembers([]);
      setDoctorOptions([]);
      setIsLoading(false);
      setError(null); // Or set a specific error if needed
      return;
    }

    setIsLoading(true);
    setError(null);

    const fetchMembers = async () => {
      try {
        const memberList = await organization.getMemberships();
        const activeMembers: MemberData[] = memberList.data.map((mem) => ({
          userId: mem.publicUserData?.userId ?? 'unknown_user_' + mem.id, // Use membership ID as fallback key
          identifier: mem.publicUserData?.identifier || 'N/A',
          firstName: mem.publicUserData?.firstName || '',
          lastName: mem.publicUserData?.lastName || '',
          role: mem.role,
          // Ensure deptIds is always an array, default to empty if null/undefined
          deptIds:
            (mem.publicMetadata?.deptIds as string[] | null | undefined) || [],
          membershipId: mem.id,
        }));

        // Filter out members with unknown userId if necessary, or handle them appropriately
        const validMembers = activeMembers.filter(
          (mem) => !mem.userId.startsWith('unknown_user_')
        );

        setMembers(validMembers);

        // ★ Filter members by selected department if ID is provided
        const filteredMembers = selectedDeptId
          ? validMembers.filter((member) =>
              member.deptIds.includes(selectedDeptId)
            )
          : validMembers;

        // Format members into doctor options
        const options = filteredMembers.map((member) => {
          const fullName =
            `${member.lastName || ''} ${member.firstName || ''}`.trim();
          const label = fullName || member.identifier; // Fallback to identifier
          return { value: member.userId, label: label || '氏名未設定' };
        });
        setDoctorOptions(options);
      } catch (err: unknown) {
        console.error('Failed to fetch organization members:', err);
        setError(
          err instanceof Error
            ? err
            : new Error('組織メンバーの取得に失敗しました。')
        );
        setMembers([]);
        setDoctorOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
    // ★ Add selectedDeptId to dependency array
  }, [organization, isOrgLoaded, selectedDeptId]);

  return { members, doctorOptions, isLoading, error };
}

// 必要に応じて組織関連の型定義を追加
export interface OrganizationSettingsPayload {
  // updateOrganizationSettings API のペイロード型
  name?: string;
  // 他の設定項目...
}

// Export types defined in types.ts
export * from './types';
